import type { Extent } from 'ol/extent';
import { createEmpty, extend } from 'ol/extent';
import type {
  LayerModel,
  VectorFeatureRecord,
  VectorGeometryType,
  VectorLayerModel,
  VectorLayerStyle,
  VectorSourceFormat,
} from '../core/types';

export const defaultVectorLayerStyle: VectorLayerStyle = {
  fillColor: '#2f80ed',
  strokeColor: '#165aa7',
  strokeWidth: 2,
  pointRadius: 6,
};

const finiteExtent = (records: readonly VectorFeatureRecord[]): readonly [number, number, number, number] | null => {
  const result: Extent = createEmpty();
  records.forEach(({ feature }) => {
    const geometry = feature.getGeometry();
    if (geometry) extend(result, geometry.getExtent());
  });
  return result.every(Number.isFinite) ? [result[0]!, result[1]!, result[2]!, result[3]!] : null;
};

const geometryTypes = (records: readonly VectorFeatureRecord[]): VectorGeometryType[] =>
  [...new Set(records.flatMap(({ feature }) => {
    const type = feature.getGeometry()?.getType();
    return type && type !== 'LinearRing' ? [type as VectorGeometryType] : [];
  }))];

export function createGenericLayer(input: {
  id: string;
  name: string;
  sourceName: string;
  sourceFormat: VectorSourceFormat;
  sourceCrs: string;
  assumedCrs: boolean;
  records: readonly VectorFeatureRecord[];
  order?: number;
  warnings?: readonly string[];
}): VectorLayerModel {
  return {
    id: input.id,
    kind: 'generic',
    name: input.name,
    sourceName: input.sourceName,
    sourceFormat: input.sourceFormat,
    sourceCrs: input.sourceCrs,
    assumedCrs: input.assumedCrs,
    geometryTypes: geometryTypes(input.records),
    attributeFields: [...new Set(input.records.flatMap(({ attributes }) => Object.keys(attributes)))],
    features: input.records,
    featuresById: new Map(input.records.map((record) => [record.id, record])),
    extent: finiteExtent(input.records),
    visible: true,
    opacity: 1,
    locked: false,
    order: input.order ?? 0,
    style: defaultVectorLayerStyle,
    warnings: input.warnings ?? [],
    scopeMode: 'none',
  };
}

export function createMasterLayer(model: LayerModel, order = 0): VectorLayerModel {
  const records = model.fragments.map(({ id, feature, attributes }) => ({ id, feature, attributes }));
  return {
    id: model.id,
    kind: 'master',
    name: 'Maestro CASUR',
    sourceName: model.name,
    sourceFormat: model.sourceFormat,
    sourceCrs: model.sourceCrs,
    assumedCrs: false,
    geometryTypes: geometryTypes(records),
    attributeFields: model.attributeFields,
    features: records,
    featuresById: new Map(records.map((record) => [record.id, record])),
    extent: finiteExtent(records),
    visible: true,
    opacity: 1,
    locked: true,
    order,
    style: defaultVectorLayerStyle,
    warnings: model.warnings,
    masterModel: model,
    scopeMode: 'attributes',
  };
}

export const normalizeLayerOrder = (layers: readonly VectorLayerModel[]): VectorLayerModel[] =>
  layers.map((layer, order) => ({ ...layer, order }));

export function updateLayer(layers: readonly VectorLayerModel[], id: string, patch: Partial<Pick<VectorLayerModel, 'name' | 'visible' | 'opacity' | 'locked' | 'style' | 'scopeMode'>>): VectorLayerModel[] {
  return layers.map((layer) => layer.id === id ? { ...layer, ...patch } : layer);
}

export function moveLayer(layers: readonly VectorLayerModel[], id: string, direction: 'up' | 'down'): VectorLayerModel[] {
  const result = [...layers];
  const index = result.findIndex((layer) => layer.id === id);
  const target = direction === 'up' ? index + 1 : index - 1;
  if (index < 0 || target < 0 || target >= result.length) return result;
  [result[index], result[target]] = [result[target]!, result[index]!];
  return normalizeLayerOrder(result);
}

export function removeExternalLayer(layers: readonly VectorLayerModel[], id: string): VectorLayerModel[] {
  const target = layers.find((layer) => layer.id === id);
  return target && target.kind !== 'master' ? normalizeLayerOrder(layers.filter((layer) => layer.id !== id)) : [...layers];
}

/** Attribute columns of planning objects, in reading order (only those used by some object are shown). */
export const PLANNING_ATTRIBUTE_FIELDS = ['Nombre', 'Tipo', 'Estado', 'Material', 'Diámetro', 'Sentido', 'Equipo / modelo', 'Potencia nominal', 'Tipo de bomba', 'Longitud (m)', 'Área (ha)', 'Etiqueta', 'Observación'] as const;

export function withLayerFeatures(layer: VectorLayerModel, records: readonly VectorFeatureRecord[]): VectorLayerModel {
  return {
    ...layer,
    geometryTypes: geometryTypes(records),
    attributeFields:
      layer.kind === 'working' && layer.planning
        ? PLANNING_ATTRIBUTE_FIELDS.filter((field) => records.some(({ attributes }) => field in attributes))
        : layer.kind === 'working'
          ? ['Nombre', 'Observación']
          : [...new Set(records.flatMap(({ attributes }) => Object.keys(attributes)))],
    features: records,
    featuresById: new Map(records.map((record) => [record.id, record])),
    extent: finiteExtent(records),
  };
}

/** 1-based record number of a Shapefile object in its .shp (traceability only; never a source attribute). */
export function sourceRecordNumber(layer: Pick<VectorLayerModel, 'sourceFormat'>, recordId: string): number | null {
  if (layer.sourceFormat !== 'SHP') return null;
  const match = /\/feature:(\d+)$/.exec(recordId);
  return match ? Number(match[1]) + 1 : null;
}
